// =============================================================================
// FILE: packages/renderer/src/lib/notifications/notificationIntelligence.ts
// PURPOSE: Modular Notification Intelligence Engine for R2 Anxiety Control.
//   Classifies incoming messages by contact priority + type, and holds
//   non-priority communications for calm, later review. Designed to later plug
//   into real email / contacts / calendar / notification providers — the
//   classify() contract stays stable.
// =============================================================================

export type Priority = 'allowed' | 'held';
export type ContactTier = 'favorite' | 'emergency' | 'important' | 'approved';
export type MessageKind = 'email' | 'notification' | 'marketing' | 'alert';

export interface PriorityContact { id: string; name: string; email: string; tier: ContactTier }
export interface IncomingMessage { id: string; sender: string; senderEmail?: string; subject: string; kind: MessageKind; ts: string }
export interface ClassifiedMessage extends IncomingMessage { priority: Priority; reason: string }

const LS_CONTACTS = 'rcd2.aci.contacts.v1';
const LS_HELD = 'rcd2.aci.held.v1';

const DEFAULT_CONTACTS: PriorityContact[] = [
  { id: 'c1', name: 'Project Owner', email: 'owner@project.com', tier: 'important' },
  { id: 'c2', name: 'Emergency Line', email: 'emergency@rcd2.local', tier: 'emergency' },
];

function readLS<T>(key: string, fallback: T): T {
  try { const raw = localStorage.getItem(key); return raw ? (JSON.parse(raw) as T) : fallback; }
  catch { return fallback; }
}
function writeLS(key: string, val: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* */ }
}

// ── Priority contacts ───────────────────────────────────────────────────────
export function getPriorityContacts(): PriorityContact[] {
  const stored = readLS<PriorityContact[] | null>(LS_CONTACTS, null);
  if (!stored) { writeLS(LS_CONTACTS, DEFAULT_CONTACTS); return DEFAULT_CONTACTS; }
  return stored;
}
export function addPriorityContact(name: string, email: string, tier: ContactTier = 'approved'): PriorityContact[] {
  const list = getPriorityContacts();
  const next = [...list, { id: crypto.randomUUID(), name: name.trim(), email: email.trim(), tier }];
  writeLS(LS_CONTACTS, next);
  return next;
}
export function removePriorityContact(id: string): PriorityContact[] {
  const next = getPriorityContacts().filter((c) => c.id !== id);
  writeLS(LS_CONTACTS, next);
  return next;
}

// ── Classification ──────────────────────────────────────────────────────────
export function classifyMessage(msg: IncomingMessage): ClassifiedMessage {
  const contacts = getPriorityContacts();
  const match = contacts.find((c) =>
    (msg.senderEmail && c.email.toLowerCase() === msg.senderEmail.toLowerCase()) ||
    c.name.toLowerCase() === msg.sender.toLowerCase());
  if (match) return { ...msg, priority: 'allowed', reason: `Priority contact · ${match.tier}` };
  if (msg.kind === 'marketing') return { ...msg, priority: 'held', reason: 'Marketing message' };
  if (msg.kind === 'alert') return { ...msg, priority: 'held', reason: 'Non-critical alert' };
  if (msg.kind === 'notification') return { ...msg, priority: 'held', reason: 'Non-priority notification' };
  return { ...msg, priority: 'held', reason: 'Unrecognized sender' };
}

/** Calm, non-interrupting phrasing for a held message. */
export function calmPhrasing(msg: ClassifiedMessage): string {
  if (msg.reason.startsWith('Marketing')) return 'A marketing message arrived and has been safely held. Would you like me to review it?';
  if (msg.reason.startsWith('Non-critical')) return 'A non-critical alert arrived and is being held quietly. Would you like me to review it?';
  return 'A message has arrived from an unrecognized sender. It has been safely held. Would you like me to review it?';
}

// ── Held queue ────────────────────────────────────────────────────────────────
export function getHeldMessages(): ClassifiedMessage[] { return readLS<ClassifiedMessage[]>(LS_HELD, []); }
export function holdMessage(msg: ClassifiedMessage): ClassifiedMessage[] {
  const next = [msg, ...getHeldMessages()].slice(0, 50);
  writeLS(LS_HELD, next);
  return next;
}
export function releaseMessage(id: string): ClassifiedMessage[] {
  const next = getHeldMessages().filter((m) => m.id !== id);
  writeLS(LS_HELD, next);
  return next;
}
export function clearHeld(): void { writeLS(LS_HELD, []); }

// ── Demo simulator (until real providers are wired) ───────────────────────────
const SAMPLE_SENDERS: IncomingMessage[] = [
  { id: '', sender: 'Unknown Sender', senderEmail: 'noreply@random.io', subject: 'You have a new connection request', kind: 'notification', ts: '' },
  { id: '', sender: 'MegaDeals', senderEmail: 'promo@megadeals.com', subject: '70% OFF — today only!', kind: 'marketing', ts: '' },
  { id: '', sender: 'System Monitor', senderEmail: 'monitor@rcd2.local', subject: 'CPU usage nominal', kind: 'alert', ts: '' },
  { id: '', sender: 'Project Owner', senderEmail: 'owner@project.com', subject: 'Re: Riser diagram approval', kind: 'email', ts: '' },
];
export function simulateIncoming(): ClassifiedMessage {
  const base = SAMPLE_SENDERS[Math.floor(Math.random() * SAMPLE_SENDERS.length)]!;
  const msg: IncomingMessage = { ...base, id: crypto.randomUUID(), ts: new Date().toISOString() };
  const classified = classifyMessage(msg);
  if (classified.priority === 'held') holdMessage(classified);
  return classified;
}
